import React, { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import ConnectionRequest from 'components/connection-request';
import Breadcrumbs from 'components/breadcrumbs';
import Tabs from 'components/tabs';
import Tab from 'components/tabs/tab';
import { useIsNotMobile, useIsMobile } from 'hooks/use-size-class';
import LoadWhenVisible from 'components/load-when-visible';
import LoadingSkeleton from 'components/loading-skeleton';
import Map from 'components/map/listing';
import SimilarListingsSections from '../listing-page/similar-listings-sections';
import WalkScore from 'components/walk-score';
import Button from 'components/control/button';
import { trackEvent, trackOffMarketPage } from 'utils/google-tag-manager';
import { buildClassName } from 'utils/build-class-name';
import { ProvinceOrStateCode, getProvinceOrStateCopy, isCanadianProvinceCode } from 'utils/province_or_state';
import styles from './style.module.scss';
import { GTM_VIEW_ADDRESS_PAGE_STANDARD_LISTING } from 'constants/events';
import { useListingRestrictions } from 'hooks/use-listing-restrictions';
import { useUserContext, useModalContext, CONNECTION_REQUEST_MOBILE_MODAL } from 'contexts';
import { validateLatitudeAndLongitude } from 'utils/geo_helper';

import type Listing from 'data/listing';
import type { Breadcrumb } from 'components/breadcrumbs';
import type Point from 'data/geojson/point/type';
import LoadingText from 'components/loading-text';

// Proper dynamic imports at module level
const NearbySchools = dynamic(() => import('components/dynamic-page/listing-page/nearby-schools'), {
  loading: () => <LoadingText />,
  ssr: false,
});

const TravelTime = dynamic(() => import('components/dynamic-page/listing-page/travel-time'), {
  loading: () => <LoadingText />,
  ssr: false,
});

const NeighbourhoodDemographics = dynamic(() => import('components/dynamic-page/listing-page/neighbourhood-demographics'), {
  loading: () => <LoadingText />,
  ssr: false,
});

type OffMarketPageProps = {
  listing: Listing;
  listings: Listing[];
  breadcrumbs: Breadcrumb[];
  isCrawler: boolean;
};

export default function OffMarketPage({ listing, breadcrumbs, isCrawler }: OffMarketPageProps) {
  const { hasNavigatedFromInternalLink } = useUserContext();
  const { openModal } = useModalContext();
  const topOfListingRef = useRef<HTMLDivElement>(null);
  const [isTabsVisible, setIsTabsVisible] = useState(false);
  const [hasFetchedSchools, setHasFetchedSchools] = useState(false);
  const [hasSchools, setHasSchools] = useState(true);
  const chartsAreLoaded = true; // Always true since components are now loaded at module level
  const isMobile = useIsMobile();
  const { province, city } = listing;
  const isCanadianListing = isCanadianProvinceCode(province?.toLowerCase() as ProvinceOrStateCode);
  const { canViewSchools } = useListingRestrictions();

  const openConnectionRequest = () => openModal(CONNECTION_REQUEST_MOBILE_MODAL, { listing });

  useEffect(() => {
    trackEvent(GTM_VIEW_ADDRESS_PAGE_STANDARD_LISTING);
  }, []);

  useEffect(() => {
    trackOffMarketPage();
  }, []);

  const street = listing.getStreet(true);

  const latitude: number | undefined = listing?.position?.latitude;
  const longitude: number | undefined = listing?.position?.longitude;
  const showWalkScore = !isCrawler && latitude && longitude && validateLatitudeAndLongitude(latitude, longitude);

  return (
    <div ref={topOfListingRef}>
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      {listing.position &&
        <div className={styles.map}>
          <Map listingId={listing!.id} position={(listing.position as unknown as Point)} className={styles.map} isInteractive={false} />
        </div>
      }
      <div className={buildClassName(styles.component, !listing.position && styles['no-position'])}>
        <div className={styles.wrapper}>
          <div className={styles['listing-content']}>
            <div className={styles['left-section']}>
              <h1 className={styles.street} itemProp={'streetAddress'}>{street}</h1>
              <div><span itemProp="addressLocality">{city}</span>{city ? ',' : ''} <span itemProp="addressRegion">{getProvinceOrStateCopy(province, city)}</span></div>
              <div className={styles['walk-score']}>
                {showWalkScore && <WalkScore latitude={latitude} longitude={longitude} />}
              </div>
              <div>
                <SimilarListingsSections listing={listing} isOffMarket={true} hasNavigatedFromInternalLink={hasNavigatedFromInternalLink} />
              </div>
              {listing.position &&
                <LoadWhenVisible onValueChange={() => setIsTabsVisible(true)}>
                  <>
                    {isTabsVisible && chartsAreLoaded ?
                      <Tabs>
                        {(!hasSchools && hasFetchedSchools) || !canViewSchools ? null :
                          <Tab label="Schools"><NearbySchools listing={listing} hasFetched={hasFetchedSchools} setHasFetched={setHasFetchedSchools} setHasSchools={setHasSchools} /></Tab>
                        }
                        {listing.neighbourhood?.id && isCanadianListing ? <Tab label="Demographics"><NeighbourhoodDemographics neighbourhoodId={listing.neighbourhood?.id} /></Tab> : null }
                        <Tab label="Commute"><TravelTime listing={listing} /></Tab>
                      </Tabs>
                      : <LoadingSkeleton height="15.7em" />
                    }
                  </>
                </LoadWhenVisible>
              }
            </div>
            {useIsNotMobile() &&
              <div className={styles['request-info-section']}>
                <ConnectionRequest listing={listing} />
              </div>
            }
          </div>
        </div>
        {isMobile &&
          <Button label="Free Consultation" theme="primary" className={styles['request-property-info']} onClick={openConnectionRequest} />
        }
      </div>
    </div>
  );
}
